import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFamilyAuth } from '../../contexts/FamilyAuthContext';
import axios from 'axios';

/**
 * Phase 17: Step 5 - Completion Screen
 * Celebrate and redirect to dashboard
 */
function Step5Done() {
  const navigate = useNavigate();
  const { getAuthHeader, refreshUser } = useFamilyAuth();
  const [completing, setCompleting] = useState(false);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    // Confetti animation trigger
    const timer = setTimeout(() => {
      triggerConfetti();
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  const triggerConfetti = () => {
    // Simple confetti effect using emojis
    const confettiCount = 30;
    const container = document.getElementById('confetti-container');
    if (!container) return;

    for (let i = 0; i < confettiCount; i++) {
      const confetti = document.createElement('div');
      confetti.className = 'confetti-item';
      confetti.textContent = ['🎉', '✨', '💙', '🎊', '⭐'][Math.floor(Math.random() * 5)];
      confetti.style.left = Math.random() * 100 + '%';
      confetti.style.animationDelay = Math.random() * 2 + 's';
      confetti.style.animationDuration = (Math.random() * 2 + 3) + 's';
      container.appendChild(confetti);
    }
  };

  const handleComplete = async () => {
    setCompleting(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/family/onboarding/complete`,
        {},
        { headers: getAuthHeader() }
      );
      
      // Refresh user data
      await refreshUser();
      
      // Redirect to dashboard
      setTimeout(() => {
        navigate('/family/dashboard');
      }, 500);
    } catch (error) {
      console.error('Error completing onboarding:', error);
      alert('Fehler beim Abschließen des Onboardings.');
      setCompleting(false);
    }
  };

  return (
    <div className="w-full max-w-2xl mx-auto animate-fade-in relative">
      {/* Confetti Container */}
      <div id="confetti-container" className="fixed inset-0 pointer-events-none z-50 overflow-hidden"></div>

      {/* Progress */}
      <div className="mb-8">
        <div className="flex justify-center gap-2">
          <div className="w-3 h-3 rounded-full bg-white"></div>
          <div className="w-3 h-3 rounded-full bg-white"></div>
          <div className="w-3 h-3 rounded-full bg-white"></div>
          <div className="w-3 h-3 rounded-full bg-white"></div>
          <div className="w-3 h-3 rounded-full bg-white"></div>
        </div>
      </div>

      {/* Card */}
      <div className="bg-neutral-900/90 backdrop-blur-xl rounded-3xl border border-neutral-600/40 p-8 sm:p-12 shadow-2xl animate-scale-in text-center">
        {/* Success Icon */}
        <div className="mb-8 relative">
          <div className="inline-flex items-center justify-center w-32 h-32 rounded-full bg-gradient-to-br from-hayat-success-500 to-hayat-success-600 shadow-2xl animate-bounce-soft">
            <span className="text-7xl">✓</span>
          </div>
        </div>

        {/* Title */}
        <h1 className="text-4xl sm:text-5xl font-bold text-[#F9FAFB] mb-4">
          Alles bereit! 🎉
        </h1>
        
        <p className="text-xl text-neutral-500 mb-8">
          Sie sind jetzt Teil der <strong>Hayat Familie</strong>.
        </p>

        {/* Feature Summary */}
        <div className="bg-hayat-primary-50 rounded-2xl p-6 mb-8 text-left">
          <h3 className="font-bold text-hayat-primary-900 mb-4 text-center">Was Sie jetzt tun können:</h3>
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <span className="text-2xl">👨‍👩‍👧</span>
              <span className="text-[#CBD5F5]">Senioren-Profile verwalten</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-2xl">📞</span>
              <span className="text-[#CBD5F5]">Video-Anrufe starten</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-2xl">⏰</span>
              <span className="text-[#CBD5F5]">Erinnerungen erstellen</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-2xl">📊</span>
              <span className="text-[#CBD5F5]">Aktivitäten verfolgen</span>
            </div>
          </div>
        </div>

        {/* CTA */}
        <button
          onClick={handleComplete}
          disabled={completing}
          className="w-full py-5 bg-gradient-to-r from-hayat-primary-500 to-hayat-primary-600 text-white rounded-2xl font-bold text-lg shadow-hayat-lg hover:shadow-hayat-xl hover:scale-105 active:scale-95 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {completing ? 'Wird abgeschlossen...' : 'Zum Dashboard 🚀'}
        </button>
      </div>

      {/* CSS for confetti animation */}
      <style>{`
        .confetti-item {
          position: absolute;
          top: -10%;
          font-size: 2rem;
          animation: confetti-fall linear forwards;
          pointer-events: none;
        }
        
        @keyframes confetti-fall {
          to {
            transform: translateY(110vh) rotate(720deg);
            opacity: 0;
          }
        }
      `}</style>
    </div>
  );
}

export default Step5Done;